import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { Search, Loader2, BookOpen, Clock, SortAsc, SortDesc, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function AdminRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('date-desc'); // date-desc, date-asc, course-asc, course-desc, student-asc, student-desc
  const [isSortOpen, setIsSortOpen] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'course_requests'), orderBy('requestedAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRequests(data);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching course requests:", error);
      setLoading(false);
    });
    return unsub;
  }, []);

  const filteredAndSortedRequests = requests
    .filter(req => {
      const term = searchTerm.toLowerCase();
      return (
        req.studentName?.toLowerCase().includes(term) ||
        req.courseName?.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      switch (sortOption) {
        case 'date-desc':
          return (b.requestedAt?.toMillis() || 0) - (a.requestedAt?.toMillis() || 0);
        case 'date-asc':
          return (a.requestedAt?.toMillis() || 0) - (b.requestedAt?.toMillis() || 0);
        case 'course-asc':
          return (a.courseName || '').localeCompare(b.courseName || '');
        case 'course-desc':
          return (b.courseName || '').localeCompare(a.courseName || '');
        case 'student-asc':
          return (a.studentName || '').localeCompare(b.studentName || '');
        case 'student-desc':
          return (b.studentName || '').localeCompare(a.studentName || '');
        default:
          return 0;
      }
    });

  const sortOptions = [
    { value: 'date-desc', label: 'الأحدث أولاً', icon: <SortDesc className="w-4 h-4" /> },
    { value: 'date-asc', label: 'الأقدم أولاً', icon: <SortAsc className="w-4 h-4" /> },
    { value: 'course-asc', label: 'الدورة (أ-ي)', icon: <SortAsc className="w-4 h-4" /> },
    { value: 'course-desc', label: 'الدورة (ي-أ)', icon: <SortDesc className="w-4 h-4" /> },
    { value: 'student-asc', label: 'الطالب (أ-ي)', icon: <SortAsc className="w-4 h-4" /> },
    { value: 'student-desc', label: 'الطالب (ي-أ)', icon: <SortDesc className="w-4 h-4" /> },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700" dir="rtl">
      
      {/* Header section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white flex items-center gap-3">
            <div className="p-3 bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 rounded-xl">
              <BookOpen className="w-7 h-7" />
            </div>
            طلبات الدورات
          </h1>
          <p className="text-slate-500 dark:text-zinc-400 mt-2 font-medium">سجل بجميع طلبات الطلاب للدورات غير المتوفرة</p>
        </div>
      </div>

      {/* Controls: Search and Sort */}
      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-2xl p-4 flex flex-col md:flex-row items-center gap-4 shadow-sm">
        <div className="relative flex-1 w-full">
          <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
            <Search className="w-5 h-5 text-slate-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="ابحث باسم الطالب أو الدورة..."
            className="w-full pl-4 pr-12 py-3 bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-medium text-slate-900 dark:text-white"
          />
        </div>

        <div className="relative shrink-0 w-full md:w-auto">
          <button
            onClick={() => setIsSortOpen(!isSortOpen)}
            className="w-full md:w-48 flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-zinc-950 border border-slate-200 dark:border-white/10 rounded-xl font-bold text-slate-700 dark:text-zinc-300 hover:border-primary-500 transition-colors"
          >
            <div className="flex items-center gap-2">
              {sortOptions.find(o => o.value === sortOption)?.icon}
              <span>{sortOptions.find(o => o.value === sortOption)?.label}</span>
            </div>
            <ChevronDown className="w-4 h-4" />
          </button>

          <AnimatePresence>
            {isSortOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setIsSortOpen(false)} />
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute top-full right-0 mt-2 w-full min-w-[200px] z-50 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-xl shadow-xl overflow-hidden py-2"
                >
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setSortOption(opt.value);
                        setIsSortOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-right text-sm font-bold transition-colors ${
                        sortOption === opt.value
                          ? 'bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400'
                          : 'text-slate-600 dark:text-zinc-400 hover:bg-slate-50 dark:hover:bg-white/5'
                      }`}
                    >
                      {opt.icon}
                      {opt.label}
                    </button>
                  ))}
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-right border-collapse min-w-[600px]">
            <thead>
              <tr className="bg-slate-50 dark:bg-zinc-950 border-b border-slate-200 dark:border-white/10 text-slate-500 dark:text-zinc-400 text-sm font-black uppercase tracking-wider">
                <th className="py-5 px-6">اسم الطالب</th>
                <th className="py-5 px-6">الدورة المطلوبة</th>
                <th className="py-5 px-6">تاريخ الطلب</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {filteredAndSortedRequests.length === 0 ? (
                <tr>
                  <td colSpan="3" className="py-12 text-center text-slate-500 dark:text-zinc-500 font-bold">
                    لا توجد طلبات تطابق بحثك.
                  </td>
                </tr>
              ) : (
                filteredAndSortedRequests.map((req) => (
                  <tr key={req.id} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors group">
                    <td className="py-4 px-6">
                      <div className="font-bold text-slate-900 dark:text-white">
                        {req.studentName}
                      </div>
                    </td>
                    <td className="py-4 px-6 min-w-[200px]">
                      <div className="font-black text-primary-600 dark:text-primary-400 text-lg">
                        {req.courseName}
                      </div>
                      {req.motivation && (
                        <div className="mt-2 text-sm text-slate-500 dark:text-zinc-400 border-r-2 border-primary-500/30 pr-3 py-1">
                          "{req.motivation}"
                        </div>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2 text-slate-500 dark:text-zinc-400 font-medium">
                        <Clock className="w-4 h-4 group-hover:text-primary-500 transition-colors" />
                        <span dir="ltr">
                          {req.requestedAt?.toMillis() ? format(req.requestedAt.toMillis(), 'dd MMM yyyy, p', { locale: ar }) : 'غير متوفر'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
